import React, { useEffect, useState } from "react";
import { getAuthToken } from "../api";
import "./StudentInsights.css";

const TRAJECTORY_ICON = { improving: "↑", stable: "→", declining: "↓" };
const TRAJECTORY_COLOR = { improving: "#4ECDC4", stable: "rgba(255,255,255,0.55)", declining: "#FF6B6B" };
const LEVEL_CONFIG = {
  PLACEMENT_READY: { label: "Placement Ready",  color: "#4ECDC4", bg: "rgba(78,205,196,0.15)" },
  ALMOST_READY:    { label: "Almost Ready",      color: "#FFD700", bg: "rgba(255,215,0,0.12)" },
  NEEDS_WORK:      { label: "Needs Work",        color: "#FF6B6B", bg: "rgba(255,107,107,0.15)" },
};
const PRIORITY_COLOR = { HIGH: "#FF6B6B", MEDIUM: "#FFD700", LOW: "rgba(255,255,255,0.45)" };

function ScoreBar({ pct, color }) {
  return (
    <div className="si-bar-track">
      <div className="si-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
    </div>
  );
}

function BreakdownCard({ title, icon, color, lines }) {
  return (
    <div className="si-breakdown-card" style={{ borderColor: color + "44" }}>
      <div className="si-breakdown-title" style={{ color }}>
        <span>{icon}</span> {title}
      </div>
      {lines.map(([k, v]) => (
        <div key={k} className="si-breakdown-row">
          <span className="si-breakdown-key">{k}</span>
          <span className="si-breakdown-val" style={{ color: v === "—" ? "rgba(255,255,255,0.35)" : "#fff" }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function StudentInsights({ user, apiBase }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const base = apiBase || process.env.REACT_APP_API_BASE || "http://localhost:8000";

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = await getAuthToken();
        const res   = await fetch(`${base}/dashboard/insights`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.message || `Error ${res.status}`);
        }
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [base]);

  if (loading) {
    return (
      <section className="si-container si-loading">
        <div className="si-spinner" />
        <p>Analysing your performance…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="si-container si-error">
        <p>Could not load insights: {error}</p>
      </section>
    );
  }

  if (!data) return null;

  const {
    campus_readiness_score  = 0,
    campus_readiness_level  = "NEEDS_WORK",
    strengths               = [],
    weaknesses              = [],
    score_breakdown         = {},
    trajectory              = {},
    focus_plan              = {},
  } = data;

  const lvl        = LEVEL_CONFIG[campus_readiness_level] || LEVEL_CONFIG.NEEDS_WORK;
  const interview  = score_breakdown.interview  || {};
  const coding     = score_breakdown.coding     || {};
  const mcq        = score_breakdown.mcq        || {};
  const tips       = focus_plan.tips            || [];
  const focusAreas = focus_plan.next_focus_areas || [];

  const fmt = (v, suffix = "") => (v != null ? `${Math.round(v)}${suffix}` : "—");

  return (
    <section className="si-container">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="si-header">
        <div>
          <p className="si-eyebrow">AI-Powered Analysis</p>
          <h2 className="si-title">Campus Placement Readiness</h2>
          <p className="si-subtitle">
            Aggregated from all your interviews, coding submissions, and MCQ tests.
          </p>
        </div>

        <div className="si-score-ring" style={{ "--ring-color": lvl.color }}>
          <svg viewBox="0 0 100 100" className="si-ring-svg">
            <circle cx="50" cy="50" r="42" className="si-ring-track" />
            <circle
              cx="50" cy="50" r="42"
              className="si-ring-fill"
              style={{
                stroke:           lvl.color,
                strokeDashoffset: `${264 - (campus_readiness_score / 100) * 264}`,
              }}
            />
          </svg>
          <div className="si-ring-inner">
            <span className="si-ring-score">{Math.round(campus_readiness_score)}</span>
            <span className="si-ring-max">/100</span>
            <span className="si-ring-label" style={{ color: lvl.color, background: lvl.bg }}>
              {lvl.label}
            </span>
          </div>
        </div>
      </div>

      {/* ── Score breakdown cards ─────────────────────────────────────────── */}
      <div className="si-breakdown-grid">
        <BreakdownCard
          title="Interview" icon="🎙" color="#8B5FBF"
          lines={[
            ["Technical",     fmt(interview.technical, "/100")],
            ["Communication", fmt(interview.communication, "/100")],
            ["Confidence",    fmt(interview.confidence, "/100")],
            ["Sessions",      interview.sessions != null ? String(interview.sessions) : "—"],
          ]}
        />
        <BreakdownCard
          title="Coding" icon="💻" color="#4ECDC4"
          lines={[
            ["Acceptance rate", fmt(coding.acceptance_rate, "%")],
            ["Problems solved", coding.problems_accepted != null ? String(coding.problems_accepted) : "—"],
            ["Total submitted", coding.total_submissions  != null ? String(coding.total_submissions)  : "—"],
          ]}
        />
        <BreakdownCard
          title="MCQ / Aptitude" icon="📝" color="#FFD700"
          lines={[
            ["Technical MCQ", fmt(mcq.technical, "%")],
            ["Aptitude",      fmt(mcq.aptitude,  "%")],
            ["Workstyle",     fmt(mcq.workstyle,  "%")],
            ["Attempts",      mcq.attempts != null ? String(mcq.attempts) : "—"],
          ]}
        />
      </div>

      {/* ── Strengths & Weaknesses ───────────────────────────────────────── */}
      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="si-sw-grid">
          {/* Strengths */}
          {strengths.length > 0 && (
            <div className="si-sw-panel">
              <div className="si-sw-panel-header si-sw-panel-header--strength">
                <span>Consistent Strengths</span>
                <span className="si-sw-badge si-sw-badge--strength">{strengths.length}</span>
              </div>
              {strengths.map((s) => (
                <div key={s.label} className="si-sw-row">
                  <div className="si-sw-label-row">
                    <span className="si-sw-label">{s.label}</span>
                    <span className="si-sw-freq" style={{ color: "#4ECDC4" }}>
                      {s.frequency}× &nbsp;
                      {trajectory.technical && (
                        <span style={{ color: TRAJECTORY_COLOR[trajectory.technical] }}>
                          {TRAJECTORY_ICON[trajectory.technical]}
                        </span>
                      )}
                    </span>
                  </div>
                  <ScoreBar pct={s.pct} color="rgba(78,205,196,0.7)" />
                </div>
              ))}
            </div>
          )}

          {/* Weaknesses */}
          {weaknesses.length > 0 && (
            <div className="si-sw-panel">
              <div className="si-sw-panel-header si-sw-panel-header--weakness">
                <span>Recurring Weaknesses</span>
                <span className="si-sw-badge si-sw-badge--weakness">{weaknesses.length}</span>
              </div>
              {weaknesses.map((w) => (
                <div key={w.label} className="si-sw-row">
                  <div className="si-sw-label-row">
                    <span className="si-sw-label">{w.label}</span>
                    <span
                      className="si-priority-chip"
                      style={{ color: PRIORITY_COLOR[w.priority], borderColor: PRIORITY_COLOR[w.priority] + "55" }}
                    >
                      {w.priority}
                    </span>
                  </div>
                  <ScoreBar pct={w.pct} color={PRIORITY_COLOR[w.priority] + "99"} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Score trajectory ─────────────────────────────────────────────── */}
      {Object.keys(trajectory).length > 0 && (
        <div className="si-trajectory-row">
          {Object.entries(trajectory).map(([dim, dir]) => (
            <div key={dim} className="si-traj-chip" style={{ borderColor: TRAJECTORY_COLOR[dir] + "66" }}>
              <span style={{ color: TRAJECTORY_COLOR[dir], fontSize: "1.1rem" }}>{TRAJECTORY_ICON[dir]}</span>
              <span className="si-traj-label">{dim.charAt(0).toUpperCase() + dim.slice(1)}</span>
              <span className="si-traj-dir" style={{ color: TRAJECTORY_COLOR[dir] }}>{dir}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── LLM Focus Plan ───────────────────────────────────────────────── */}
      {tips.length > 0 && (
        <div className="si-focus-plan">
          <div className="si-focus-header">
            <div>
              <p className="si-focus-eyebrow">AI Mentor</p>
              <h3 className="si-focus-title">Your Personalised Focus Plan</h3>
            </div>
            {focus_plan.generated_at && (
              <span className="si-focus-stamp">
                Updated {new Date(focus_plan.generated_at).toLocaleDateString()}
              </span>
            )}
          </div>

          <ol className="si-tips-list">
            {tips.map((tip, i) => (
              <li key={i} className="si-tip-item">
                <span className="si-tip-num">{String(i + 1).padStart(2, "0")}</span>
                <p className="si-tip-text">{tip}</p>
              </li>
            ))}
          </ol>

          {focusAreas.length > 0 && (
            <div className="si-focus-areas">
              <p className="si-focus-areas-label">Next focus areas:</p>
              <div className="si-focus-chips">
                {focusAreas.map((area) => (
                  <span key={area} className="si-focus-chip">{area}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {strengths.length === 0 && weaknesses.length === 0 && tips.length <= 1 && (
        <div className="si-empty">
          <p>Complete at least one interview to unlock strength/weakness analysis and your personalised focus plan.</p>
        </div>
      )}
    </section>
  );
}
